import { Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';

import { DateMomentPipe } from '../../../../pipes';
import { AppStateHolder, TimeLine } from '../../../../shared';
import { UserActivitiesService } from '../user-activities.service';

@Component({
    selector: 'hour-chart',
    templateUrl: 'activity-hourChart.component.html',
    styleUrls: ['activity-hourChart.component.scss'],
    standalone: true,
    imports: [TooltipModule, DecimalPipe, ProgressSpinnerModule, DatePipe, TranslateModule, DateMomentPipe]
})

export class ActivityHourChartComponent implements OnInit, OnDestroy {
  private appHolder = inject(AppStateHolder);
  private timeLine = inject(TimeLine);
  private service = inject(UserActivitiesService);

  /** Reference to the accessible data table for focus management */
  @ViewChild('hourDataTable') hourDataTable: ElementRef<HTMLTableElement>;

  @Input() modify: Boolean;
  @Input() idx: number;
  @Input() border: boolean;
  @Input() params: string;
  @Input() changedParams: any;
  @Input() widgetPosition: any;

  @Output() dimensionSelected: EventEmitter<any> = new EventEmitter();
  @Output() popup: EventEmitter<{ widx: number, pop: boolean, params: any }> = new EventEmitter();

  requestsSubscribe: Subscription;
  status: string;
  activities: any[];
  openOptions: boolean = false;
  optionSelected: any;

  finalDate: string;
  initialDate: string;

  totalHours: number;
  closedSum: number;
  totalTime: number;
  esTime: number;
  eftTime: number;
  workedActivityTimeSum: number;
  totalActivityTimeSum: number;
  shortDate: any = [{ format: "L" }];

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
  }

  ngOnInit(): void {
    if (!this.modify) {
      this.timeLine.init();

      this.workedActivityTimeSum = 0;
      this.totalActivityTimeSum = 0;
      this.totalTime = 0;
      this.eftTime = 0;
      this.esTime = 0;

      this.loadActivities(this.params);
    }
  }

  loadActivities(params: string) {

    this.status = 'busy';

    // Default applicati sempre: periodo settimana, nessuna tipologia.
    // Vengono sovrascritti solo se l'utente ha salvato parametri validi.
    let dateType = 1; // Week
    let state = null;
    let typologies = null;

    const prms = this.parseParams(params);
    if (prms) {
      if (prms.date == 'Day')
        dateType = 0;
      else if (prms.date == 'Week')
        dateType = 1;
      else if (prms.date == 'Month')
        dateType = 2;
      else if (prms.date == 'all')
        dateType = 1;

      if (prms.state)
        state = prms.state;
      if (prms.typologies)
        typologies = prms.typologies;
    }

    // Calcola sempre initialDate/finalDate: senza un periodo valido la GET
    // non partirebbe e il widget resterebbe bloccato sullo spinner.
    this.dataClick(dateType);

    this.requestsSubscribe = this.service.getUserActivitiesFil(this.initialDate, this.finalDate, state, typologies)
      .subscribe({
        next: res => {
          this.status = 'ready';
          this.activities = res ?? [];
          this.timeCalculation();
        },
        error: () => {
          // Senza error handler lo status resterebbe 'busy' = spinner infinito.
          this.status = 'ready';
          this.activities = [];
          this.timeCalculation();
        }
      });
  }

  /**
   * Deserializza in modo difensivo i parametri salvati del widget.
   * Gestisce il doppio wrapping ({ params: "..." }) e i valori non validi
   * (oggetti gia deserializzati, "null", JSON malformato) ritornando null,
   * cosi da ricadere sui default senza far fallire il caricamento.
   */
  private parseParams(params: any): any {
    if (!params) return null;

    try {
      let prms = typeof params === 'string' ? JSON.parse(params) : params;
      if (prms && prms.params) {
        prms = typeof prms.params === 'string' ? JSON.parse(prms.params) : prms.params;
      }
      return prms ?? null;
    } catch {
      return null;
    }
  }

  timeCalculation() {

    this.activities.forEach((r) => {
      if (r.effectiveTime != null && r.effectiveTime > 0) {
        this.totalActivityTimeSum += r.effectiveTime;
        if (r.workingState != 0) //open
          this.workedActivityTimeSum += r.effectiveTime;
      }
      else if (r.estimatedTime != null && r.estimatedTime > 0) {
        this.totalActivityTimeSum += r.estimatedTime;
        if (r.workingState != 0) //open
          this.workedActivityTimeSum += r.estimatedTime;
      }
    })
  }

  hasActivities(): Boolean {

    if (this.activities === undefined)
      return false;

    return (this.activities.length > 0);
  }

  options() {
    this.openOptions = !this.openOptions;
  }

  selected() {
    this.dimensionSelected.next(this.optionSelected);
  }

  actpop() {
    if (this.changedParams == undefined) {
      this.sendParams();
    }
    else {
      let val = this.appHolder.hourParams.filter(r => { return r.indx == this.idx });

      if (val.length > 0) {
        this.popup.next({ widx: this.idx, pop: true, params: JSON.parse(val[0].params) });
      }
      else {
        this.sendParams();
      }
    }
  }

  sendParams() {

    if (this.params) {
      this.popup.next({ widx: this.idx, pop: true, params: JSON.parse(this.params) });
    }
    else {
      this.popup.next({ widx: this.idx, pop: true, params: this.params });
    }
  }

  dataClick(dateType: number) {

    let day;
    let week;
    let month;

    switch (dateType) {
      case 0:

        day = this.timeLine.day.date;

        this.finalDate = this.initialDate = (day.getFullYear() + "-" + (day.getMonth() + 1) + "-" + day.getDate());
        break;
      case 1:

        week = this.timeLine.week;
        let d1 = week.days[0].date;
        let d2 = week.days[week.days.length - 1].date;

        this.initialDate = d1.getFullYear() + "-" + (d1.getMonth() + 1) + "-" + d1.getDate();
        this.finalDate = d2.getFullYear() + "-" + (d2.getMonth() + 1) + "-" + d2.getDate();

        break;

      case 2:

        this.initialDate = this.timeLine.month.date.getFullYear() + "-" + (this.timeLine.month.date.getMonth() + 1) + "-" + this.timeLine.month.date.getDate();
        this.finalDate = this.timeLine.month.lastDate.getFullYear() + "-" + (this.timeLine.month.lastDate.getMonth() + 1) + "-" + this.timeLine.month.lastDate.getDate();

        break;

      case 3:

        this.initialDate = null;
        this.finalDate = null;

        break;
    }
  }

  ngOnDestroy(): void {
    if (this.requestsSubscribe)
      this.requestsSubscribe.unsubscribe()
  }

  /**
   * Focuses the accessible data table for screen reader users.
   * This method is called when users activate the skip link.
   */
  focusDataTable(): void {
    if (this.hourDataTable?.nativeElement) {
      this.hourDataTable.nativeElement.focus();
    }
  }
}

